// src/pages/PositionSettings/PositionSettingsModal.jsx

import React, { useState } from "react";
import { Modal, Drawer, Form, Input, InputNumber, Radio, Tooltip, Button } from "antd";
import { PlusOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import "../../components/AddUserModal/style.css";
import "./positionSettingsScoped.css";

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";

const { Group: RadioGroup, Button: RadioButton } = Radio;

const PROMO_TYPES = [
    { label: "Only Crystals", value: "onlyCrystals" },
    { label: "Crystals & Crypto", value: "crystalsAndCrypto" },
    { label: "Crypto Only", value: "cryptoOnly" },
];

const TOKEN_TYPES = [
    { label: "USDT", value: "USDT" },
    { label: "CRYSTAL", value: "CRYSTAL" },
];

const inputNumGlass =
    "w-full !rounded-xl [&_.ant-input-number-input]:!text-white/90 [&_.ant-input-number-handler-wrap]:!border-white/10";

const inputGlass =
    "!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/90 hover:!border-white/[0.18] focus:!border-emerald-400/50";

const gemRadioBtnClass =
    "!h-10 !w-10 !p-0 !rounded-xl !border-white/12 !bg-white/[0.06] overflow-hidden";

// Секция-карточка с заголовком
function Section({ title, extra, children }) {
    return (
        <div className="mb-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="mb-3 flex items-center justify-between">
                <span className="text-[12px] font-semibold uppercase tracking-wide text-white/45">{title}</span>
                {extra}
            </div>
            {children}
        </div>
    );
}

// Та же целочисленная математика, что и на бэке (promo-service.normalizeCrystalCategory):
// победители округляются (остаток — в последний тир), дроп — целые кристаллы (мин. 1),
// бюджет тира = winners * dropPerWinner
const computeCrystalPreview = (presets, totalBudget, totalPlaces) => {
    const rows = (presets || []).filter((p) => p && (p.win || p.budget_percent));
    if (!rows.length || !totalBudget || !totalPlaces) return null;

    const winners = rows.map((p) =>
        Math.max(1, Math.round(((parseFloat(p.win) || 0) / 100) * totalPlaces))
    );
    const sumW = winners.reduce((s, w) => s + w, 0);
    winners[winners.length - 1] = Math.max(0, winners[winners.length - 1] + (totalPlaces - sumW));

    const tiers = rows.map((p, i) => {
        const budget = (((parseFloat(p.budget_percent) || 0) / 100) * totalBudget);
        const drop = winners[i] > 0 ? Math.max(1, Math.round(budget / winners[i])) : 0;
        return { winners: winners[i], drop, budget: winners[i] * drop };
    });

    return {
        tiers,
        totalWinners: tiers.reduce((s, t) => s + t.winners, 0),
        totalCrystals: tiers.reduce((s, t) => s + t.budget, 0),
    };
};

export default function PositionSettingsModal({ open, onCancel, onFinish, form, editingRecord, gemTypes }) {
    const promoTypeValue = Form.useWatch("promoType", form);
    const presetsValue = Form.useWatch("presets", form);
    const budgetValue = Form.useWatch("defaultBudget", form);
    const placesValue = Form.useWatch("minPrizePlacesCount", form);

    const [previewOpen, setPreviewOpen] = useState(false);

    const crystalPreview =
        promoTypeValue === "onlyCrystals"
            ? computeCrystalPreview(presetsValue, Number(budgetValue) || 0, Number(placesValue) || 0)
            : null;

    // Суммы процентов пресетов — подсветка приближения к 100%
    const sumWin = (presetsValue || []).reduce((s, p) => s + (parseFloat(p?.win) || 0), 0);
    const sumBudget = (presetsValue || []).reduce((s, p) => s + (parseFloat(p?.budget_percent) || 0), 0);
    const pctTone = (v) => (Math.round(v) === 100 ? "text-emerald-300" : "text-amber-300/90");

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            className="custom-modal"
            width={820}
            title={editingRecord ? "Редактировать настройку" : "Новая настройка"}
            destroyOnClose
            footer={[
                <Button
                    key="cancel"
                    onClick={onCancel}
                    className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10"
                >
                    Отмена
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={() => form.submit()}
                    className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                >
                    {editingRecord ? "Обновить" : "Создать"}
                </Button>,
            ]}
        >
            <div className="position-settings-scope">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        presets: [{ win: 0, budget_percent: 0 }],
                        maxTasks: 5,
                        ...(editingRecord || {}),
                    }}
                >
                    <Section
                        title="Тип гема"
                        extra={
                            promoTypeValue === "onlyCrystals" && (
                                <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => setPreviewOpen(true)}
                                    className="!rounded-lg !border-emerald-400/30 !bg-emerald-500/10 !text-emerald-200 hover:!bg-emerald-500/20"
                                >
                                    Превью выдачи
                                </Button>
                            )
                        }
                    >
                        <Form.Item
                            name="gemId"
                            rules={[{ required: true, message: "Выберите тип гема" }]}
                            className="!mb-0"
                        >
                            <RadioGroup className="flex flex-wrap gap-2">
                                {gemTypes.map((gem) => (
                                    <RadioButton key={gem.id} value={gem.id} className={gemRadioBtnClass}>
                                        <Tooltip title={gem.name}>
                                            <div className="flex h-10 w-10 items-center justify-center">
                                                <img
                                                    src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                    alt=""
                                                    className="h-7 w-7 object-contain"
                                                />
                                            </div>
                                        </Tooltip>
                                    </RadioButton>
                                ))}
                            </RadioGroup>
                        </Form.Item>
                    </Section>

                    <Section title="Основные параметры">
                        <div className="grid grid-cols-1 gap-x-5 sm:grid-cols-2">
                            <Form.Item
                                name="number"
                                label="Число (3/5/7)"
                                rules={[{ required: true, message: "Введите число (3/5/7)" }]}
                            >
                                <InputNumber min={1} className={inputNumGlass} placeholder="3, 5 или 7" size="large" />
                            </Form.Item>
                            <Form.Item
                                name="name"
                                label="Название категории"
                                rules={[{ required: true, message: "Введите название категории" }]}
                            >
                                <Input className={inputGlass} placeholder="Например, 7 Basic" size="large" />
                            </Form.Item>
                            <Form.Item
                                name="minParticipantsCount"
                                label="Мин. участников"
                                rules={[{ required: true, message: "Укажите мин. количество участников" }]}
                            >
                                <InputNumber min={0} className={inputNumGlass} placeholder="200" size="large" />
                            </Form.Item>
                            <Form.Item
                                name="minPrizePlacesCount"
                                label="Мин. призовых мест"
                                rules={[{ required: true, message: "Укажите мин. количество призовых мест" }]}
                            >
                                <InputNumber min={0} className={inputNumGlass} placeholder="20" size="large" />
                            </Form.Item>
                            <Form.Item
                                name="promoType"
                                label="Тип промо"
                                rules={[{ required: true, message: "Выберите тип промо" }]}
                            >
                                <RadioGroup className="flex flex-wrap gap-0">
                                    {PROMO_TYPES.map((pt) => (
                                        <RadioButton key={pt.value} value={pt.value} className="!text-[12px]">
                                            {pt.label}
                                        </RadioButton>
                                    ))}
                                </RadioGroup>
                            </Form.Item>
                            <Form.Item
                                name="defaultTokenType"
                                label="Тип токена (дефолт)"
                                rules={[{ required: true, message: "Выберите тип токена" }]}
                            >
                                <RadioGroup className="flex flex-wrap gap-2">
                                    {TOKEN_TYPES.map((tt) => (
                                        <RadioButton key={tt.value} value={tt.value}>
                                            {tt.label}
                                        </RadioButton>
                                    ))}
                                </RadioGroup>
                            </Form.Item>
                            <Form.Item
                                name="defaultBudget"
                                label="Дефолтный бюджет"
                                rules={[{ required: true, message: "Введите дефолтный бюджет" }]}
                            >
                                <InputNumber
                                    min={0}
                                    className={inputNumGlass}
                                    placeholder="500.00"
                                    formatter={(value) => `${value}`}
                                    parser={(value) => parseFloat(value)}
                                    size="large"
                                />
                            </Form.Item>
                            <Form.Item
                                name="maxTasks"
                                label="Макс. задач у промоутера"
                                tooltip="Сколько кастомных задач промоутер может создать в мини-аппке. Обязательные (default) таски в лимит не входят."
                            >
                                <InputNumber min={0} className={inputNumGlass} placeholder="5" size="large" />
                            </Form.Item>
                        </div>
                    </Section>

                    <Section
                        title="Presets (проценты)"
                        extra={
                            <span className="text-[11px] text-white/40">
                                Σ Winners <b className={pctTone(sumWin)}>{Math.round(sumWin * 10) / 10}%</b>
                                <span className="mx-2 text-white/20">·</span>
                                Σ Budget <b className={pctTone(sumBudget)}>{Math.round(sumBudget * 10) / 10}%</b>
                            </span>
                        }
                    >
                        {/* заголовки колонок один раз */}
                        <div className="mb-1 grid grid-cols-[28px_1fr_1fr_36px] items-center gap-3 px-1 text-[11px] uppercase tracking-wide text-white/35">
                            <div>#</div>
                            <div>Winners %</div>
                            <div>Budget %</div>
                            <div />
                        </div>

                        <Form.List name="presets">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((field, idx) => (
                                        <div
                                            key={field.key}
                                            className="grid grid-cols-[28px_1fr_1fr_36px] items-center gap-3 rounded-xl px-1 py-1 hover:bg-white/[0.02]"
                                        >
                                            <div className="text-center text-[13px] font-medium text-white/40">
                                                {idx + 1}
                                            </div>
                                            <Form.Item
                                                {...field}
                                                name={[field.name, "win"]}
                                                rules={[{ required: true, message: "" }]}
                                                className="!mb-0"
                                            >
                                                <InputNumber
                                                    min={0}
                                                    max={100}
                                                    step={0.1}
                                                    className={inputNumGlass}
                                                    placeholder="% победителей"
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                {...field}
                                                name={[field.name, "budget_percent"]}
                                                rules={[{ required: true, message: "" }]}
                                                className="!mb-0"
                                            >
                                                <InputNumber
                                                    min={0}
                                                    max={100}
                                                    step={0.1}
                                                    className={inputNumGlass}
                                                    placeholder="% бюджета"
                                                />
                                            </Form.Item>
                                            <Button
                                                type="text"
                                                danger
                                                disabled={fields.length <= 1}
                                                onClick={() => remove(field.name)}
                                                icon={<DeleteOutlined />}
                                                className="!text-rose-400 hover:!bg-rose-500/10 disabled:!opacity-25"
                                            />
                                        </div>
                                    ))}
                                    <Button
                                        type="dashed"
                                        onClick={() => add({ win: 0, budget_percent: 0 })}
                                        block
                                        icon={<PlusOutlined />}
                                        className="!mt-3 !rounded-xl !border-white/15 !bg-white/[0.04] !text-white/80 hover:!border-emerald-400/40 hover:!text-emerald-200"
                                    >
                                        Добавить preset
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </Section>

                </Form>

                <Drawer
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    placement="right"
                    width={440}
                    zIndex={1100}
                    className="position-settings-scope"
                    title={<span className="text-white/90">Превью выдачи (Only Crystals)</span>}
                    styles={{ body: { padding: 16 }, header: { background: "transparent" }, content: { background: "#0b0f17" } }}
                >
                    {crystalPreview ? (
                        <>
                            <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
                                <div className="grid grid-cols-4 gap-2 border-b border-white/10 px-3 py-2 text-[12px] text-white/45">
                                    <div>Кристалл</div>
                                    <div className="text-right">Игроков</div>
                                    <div className="text-right">По сколько</div>
                                    <div className="text-right">Всего</div>
                                </div>
                                {crystalPreview.tiers.map((tier, i) => {
                                    // тир i (сверху) соответствует гему с position = N - i (как в award-service)
                                    const gemPos = crystalPreview.tiers.length - i;
                                    const gem = (gemTypes || []).find((g) => Number(g.position) === gemPos);
                                    return (
                                        <div
                                            key={i}
                                            className="grid grid-cols-4 items-center gap-2 border-b border-white/[0.06] px-3 py-1.5 text-[13px] text-white/85"
                                        >
                                            <div className="flex items-center gap-2">
                                                {gem?.frontImage && (
                                                    <img
                                                        src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                        alt=""
                                                        className="h-6 w-6 object-contain"
                                                    />
                                                )}
                                                <span className="text-white/70">
                                                    {gem?.name || `Позиция ${gemPos}`}
                                                </span>
                                            </div>
                                            <div className="text-right">{tier.winners}</div>
                                            <div className="text-right font-medium text-amber-200/95">
                                                {tier.drop} шт
                                            </div>
                                            <div className="text-right">{tier.budget}</div>
                                        </div>
                                    );
                                })}
                                <div className="grid grid-cols-4 gap-2 px-3 py-2 text-[13px] font-medium text-white/90">
                                    <div>Итого</div>
                                    <div className="text-right">{crystalPreview.totalWinners}</div>
                                    <div className="text-right">—</div>
                                    <div className="text-right">{crystalPreview.totalCrystals}</div>
                                </div>
                            </div>
                            {Number(budgetValue) > 0 &&
                                crystalPreview.totalCrystals !== Number(budgetValue) && (
                                    <p className="mt-3 text-[12px] text-amber-300/80">
                                        Из-за округления до целых кристаллов фактическая выдача (
                                        {crystalPreview.totalCrystals}) отличается от бюджета (
                                        {Number(budgetValue)}). Это ожидаемо — на выдаче используются
                                        именно целые значения.
                                    </p>
                                )}
                        </>
                    ) : (
                        <p className="text-[13px] text-white/45">
                            Заполни дефолтный бюджет, мин. призовых мест и пресеты — превью посчитается автоматически.
                        </p>
                    )}
                </Drawer>
            </div>
        </Modal>
    );
}
